import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight, ShieldCheck } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { usePageTitle } from '@/hooks/use-page-title'
import { signUp } from '@/lib/auth-client'

const RegisterSchema = z.object({
	name: z.string().min(1, 'Jméno je povinné'),
	username: z.string().min(3, 'Uživatelské jméno musí mít alespoň 3 znaky'),
	email: z.string().email('Neplatný e-mail'),
	password: z.string().min(8, 'Heslo musí mít alespoň 8 znaků'),
})

type RegisterFormData = z.infer<typeof RegisterSchema>

export function RegisterPage() {
	usePageTitle('Registrace')
	const navigate = useNavigate()
	const [error, setError] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(false)

	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
	} = useForm<RegisterFormData>({
		// zodResolver's types brand against zod v4 core minor: 0 while our zod
		// ships with minor: 3; runtime is fine, cast silences the mismatch.
		resolver: zodResolver(RegisterSchema as never),
	})

	const passwordValue = useWatch({
		control,
		name: 'password',
		defaultValue: '',
	})

	const passwordStrength = useMemo(() => {
		if (!passwordValue) return { score: 0, label: '', color: '' }
		let score = 0
		if (passwordValue.length >= 8) score++
		if (passwordValue.length >= 12) score++
		if (/[A-Z]/.test(passwordValue)) score++
		if (/[0-9]/.test(passwordValue)) score++
		if (/[^A-Za-z0-9]/.test(passwordValue)) score++

		if (score <= 2) return { score: Math.max(1, score), label: 'Slabé', color: 'bg-red-500' }
		if (score <= 3) return { score: 3, label: 'Střední', color: 'bg-amber-500' }
		if (score <= 4) return { score: 4, label: 'Silné', color: 'bg-green-500' }
		return { score: 5, label: 'Velmi silné', color: 'bg-green-600' }
	}, [passwordValue])

	const onSubmit = async (data: RegisterFormData) => {
		setIsLoading(true)
		setError(null)

		try {
			const result = await signUp.email({
				name: data.name,
				username: data.username,
				email: data.email,
				password: data.password,
			})

			if (result.error) {
				if (result.error.status === 403) {
					navigate('/access-denied', { replace: true })
					return
				}
				setError(result.error.message || 'Registrace se nezdařila')
				return
			}

			navigate('/admin')
		} catch (_err) {
			setError('Nastala chyba. Zkuste to prosím znovu.')
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="min-h-screen flex flex-col bg-white">
			{/* Top bar */}
			<div className="border-b border-gray-200 bg-white">
				<div className="max-w-screen-md mx-auto px-6 py-4 flex items-center justify-between">
					<img src="/agnis_logo_crop.png" alt="Agnis" className="h-8" />
					<div className="flex items-center gap-2 text-xs text-gray-400">
						<ShieldCheck className="h-3.5 w-3.5" />
						<span>Zabezpečená registrace</span>
					</div>
				</div>
			</div>

			{/* Main */}
			<div className="flex-1 flex items-center justify-center px-4 py-12">
				<div className="w-full max-w-sm">
					{/* Heading */}
					<div className="text-center mb-8">
						<h1 className="text-2xl font-bold tracking-tight text-gray-900">
							Vytvořit účet
						</h1>
						<p className="text-sm text-gray-500 mt-2">
							Zaregistrujte se pro přístup do administrace.
						</p>
					</div>

					{/* Form */}
					<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
						<div className="space-y-2">
							<label
								htmlFor="name"
								className="text-sm font-medium text-gray-700"
							>
								Celé jméno
							</label>
							<Input
								id="name"
								type="text"
								autoComplete="name"
								autoFocus
								placeholder="Vaše jméno"
								className="h-11 bg-white border-gray-300 text-gray-900 focus-visible:ring-red-500"
								{...register('name')}
							/>
							{errors.name && (
								<p className="text-red-600 text-sm">{errors.name.message}</p>
							)}
						</div>

						<div className="space-y-2">
							<label
								htmlFor="username"
								className="text-sm font-medium text-gray-700"
							>
								Uživatelské jméno
							</label>
							<Input
								id="username"
								type="text"
								autoComplete="username"
								placeholder="Zvolte si přezdívku"
								className="h-11 bg-white border-gray-300 text-gray-900 focus-visible:ring-red-500"
								{...register('username')}
							/>
							{errors.username && (
								<p className="text-red-600 text-sm">
									{errors.username.message}
								</p>
							)}
						</div>

						<div className="space-y-2">
							<label
								htmlFor="email"
								className="text-sm font-medium text-gray-700"
							>
								E-mail
							</label>
							<Input
								id="email"
								type="email"
								autoComplete="email"
								placeholder="vas.email@priklad.cz"
								className="h-11 bg-white border-gray-300 text-gray-900 focus-visible:ring-red-500"
								{...register('email')}
							/>
							{errors.email && (
								<p className="text-red-600 text-sm">{errors.email.message}</p>
							)}
						</div>

						<div className="space-y-2">
							<label
								htmlFor="password"
								className="text-sm font-medium text-gray-700"
							>
								Heslo
							</label>
							<Input
								id="password"
								type="password"
								autoComplete="new-password"
								placeholder="Alespoň 8 znaků"
								className="h-11 bg-white border-gray-300 text-gray-900 focus-visible:ring-red-500"
								{...register('password')}
							/>
							{passwordValue && (
								<div className="space-y-1.5 pt-1">
									<div className="flex gap-1">
										{[1, 2, 3, 4, 5].map((level) => (
											<div
												key={level}
												className={`h-1.5 flex-1 rounded-full transition-colors ${
													level <= passwordStrength.score
														? passwordStrength.color
														: 'bg-gray-200'
												}`}
											/>
										))}
									</div>
									<p
										className={`text-xs ${
											passwordStrength.score <= 2
												? 'text-red-600'
												: passwordStrength.score <= 3
													? 'text-amber-600'
													: 'text-green-600'
										}`}
									>
										{passwordStrength.label}
									</p>
								</div>
							)}
							{errors.password && (
								<p className="text-red-600 text-sm">
									{errors.password.message}
								</p>
							)}
						</div>

						{error && (
							<div className="rounded-lg bg-red-50 border border-red-200 p-3">
								<p className="text-red-700 text-sm">{error}</p>
							</div>
						)}

						<Button
							type="submit"
							className="w-full h-11"
							disabled={isLoading}
						>
							{isLoading ? (
								<>
									<LoadingSpinner size="sm" className="mr-2" />
									Vytváření účtu...
								</>
							) : (
								<>
									Vytvořit účet
									<ArrowRight className="ml-2 h-4 w-4" />
								</>
							)}
						</Button>
					</form>

					{/* Login link */}
					<p className="mt-6 text-center text-sm text-gray-500">
						Už máte účet?{' '}
						<Link
							to="/login"
							className="text-red-600 font-medium hover:text-red-700 hover:underline"
						>
							Přihlaste se
						</Link>
					</p>

					{/* Footer note */}
					<p className="mt-4 text-center text-xs text-gray-400">
						Registrace je omezena na autorizované administrátory.
					</p>
				</div>
			</div>
		</div>
	)
}
